import { signInWithRedirect } from 'aws-amplify/auth'
import React from 'react'

import { enabledAuthProviders } from '../../config/auth'

import styles from './LoginModal.module.css'

import { Button } from '@/components/ui/base'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
  message?: string
}

// package-local: the brands the login UI can render. Mirrors the providers configured in CDK +
// `enabledAuthProviders` (config/auth.ts). LinkedIn is a generic OIDC provider in Cognito, so it is
// referenced by its custom provider name.
type Brand = 'amazon' | 'google' | 'facebook' | 'apple' | 'linkedin'

interface ProviderMeta {
  brand: Brand
  label: string
  /** Amplify `signInWithRedirect` provider argument. */
  provider: 'Amazon' | 'Google' | 'Facebook' | 'Apple' | { custom: string }
  icon: React.ReactNode
}

// Registry of every supported provider's display metadata. Which ones actually render is driven by
// `enabledAuthProviders`, so a provider not enabled for the current stage never shows a button.
const PROVIDER_REGISTRY: ProviderMeta[] = [
  { brand: 'amazon', label: 'Continue with Amazon', provider: 'Amazon', icon: <AmazonIcon /> },
  { brand: 'google', label: 'Continue with Google', provider: 'Google', icon: <GoogleIcon /> },
  { brand: 'facebook', label: 'Continue with Facebook', provider: 'Facebook', icon: <FacebookIcon /> },
  { brand: 'apple', label: 'Continue with Apple', provider: 'Apple', icon: <AppleIcon /> },
  { brand: 'linkedin', label: 'Continue with LinkedIn', provider: { custom: 'LinkedIn' }, icon: <LinkedInIcon /> },
]

/** Normalize an Amplify provider value to a stable key for matching against the enabled list. */
function providerKey(provider: ProviderMeta['provider']): string {
  return typeof provider === 'string' ? provider : `custom:${provider.custom}`
}

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const [loadingBrand, setLoadingBrand] = React.useState<Brand | null>(null)

  // Resolve which providers to show for this environment, preserving registry display order.
  const enabledKeys = new Set(enabledAuthProviders.map((p) => (typeof p === 'string' ? p : `custom:${p.custom}`)))
  const visibleProviders = PROVIDER_REGISTRY.filter((p) => enabledKeys.has(providerKey(p.provider)))

  const handleLogin = React.useCallback(async (meta: ProviderMeta): Promise<void> => {
    try {
      setLoadingBrand(meta.brand)
      await signInWithRedirect({ provider: meta.provider })
      // Redirect happens; no need to unset loading on success.
    } catch (error: unknown) {
      console.error(`[LoginModal] Error signing in with ${meta.brand}:`, error)
      setLoadingBrand(null)
    }
  }, [])

  if (!isOpen) return null

  return (
    <div
      className={styles.modalOverlay}
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <div className={styles.modalContent} role="dialog" aria-modal="true">
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close sign-in modal">
          ×
        </button>
        <div className={styles.panel}>
          <div className={styles.header}>
            <h2 className={styles.title}>Welcome back</h2>
            <p className={styles.subtitle}>
              Sign in to follow creators, save favorites, and join the crafting community.
            </p>
          </div>

          <div className={styles.buttons}>
            {visibleProviders.map((meta) => {
              const loading = loadingBrand === meta.brand
              const isDisabled = loadingBrand !== null
              return (
                <button
                  key={meta.brand}
                  type="button"
                  onClick={isDisabled ? undefined : () => handleLogin(meta)}
                  className={[
                    styles.providerButton,
                    styles[`brand_${meta.brand}`],
                    loading ? styles.isLoading : '',
                  ].join(' ')}
                  aria-disabled={isDisabled}
                  disabled={isDisabled}
                >
                  <span className={styles.iconWrap} aria-hidden="true">
                    {meta.icon}
                  </span>
                  <span className={styles.label}>{meta.label}</span>
                  {loading && <span className={styles.spinner} aria-hidden="true" />}
                </button>
              )
            })}
          </div>

          <div className={styles.footer}>
            <p className={styles.disclaimer}>
              By continuing, you agree to our{' '}
              <a href="/terms" className={styles.link}>
                Terms
              </a>{' '}
              and{' '}
              <a href="/privacy" className={styles.link}>
                Privacy Policy
              </a>
              .
            </p>
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Brand icons (inline SVG: size- and type-optimized vectors) ──────────────
// NOTE (compliance): Apple and Google have strict sign-in button branding guidelines. These inline
// marks are accurate official glyphs; when the Apple/Google developer accounts are set up, swap in
// their official brand-kit assets if their guidelines require exact button treatments.

function AmazonIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M11.2 4.2c-2.1 0-3.6 1.2-3.6 3 0 1.2.8 2.2 2.1 2.6 1.1.4 1.3.6 1.3 1 0 .6-.4.9-1.2.9-.7 0-1.6-.3-2.3-.7-.2-.1-.4 0-.4.2v1.5c0 .2.1.3.3.4.9.3 1.6.5 2.5.5 2.1 0 3.5-1.2 3.5-3.1 0-1.5-.7-2.4-2.2-2.8-1.1-.3-1.2-.5-1.2-.9 0-.4.3-.8 1.1-.8.7 0 1.3.2 1.9.5.2.1.4 0 .4-.2V4.8c0-.2-.1-.3-.3-.4-.7-.2-1.3-.2-1.9-.2z"
      />
      <path
        fill="#FF9900"
        d="M21 16.5c-3.1 2.2-7.4 3.4-11.6 3.4-4.5 0-8.3-1.3-9.4-2.6-.2-.2.1-.5.4-.4 3 1.4 6.2 2.1 9.8 2.1 3.8 0 7.3-.8 10-2.3.4-.2.8.3.4.6z"
      />
      <path
        fill="#FF9900"
        d="M22.2 14.9c-.3-.3-1.9-1.6-2.6-1.9-.2-.1-.5-.1-.7.1-.9.8-2.2 1.6-3.6 2.1-.2.1-.2.3 0 .4.9.3 2 .7 2.9 1.1.2.1.5 0 .6-.1.8-.7 2.1-2 2.8-2.5.2-.2.2-.5.1-.7z"
      />
    </svg>
  )
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.9 0 7.4 1.4 10.1 3.7l6.8-6.8C35.9 2.6 30.3 0 24 0 14.6 0 6.4 5.4 2.5 13.2l7.9 6.1C12.4 13.8 17.7 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.6c-.5 2.6-2 4.8-4.2 6.3v5.2h6.8c4-3.6 6.3-8.9 6.3-15.8z"
      />
      <path fill="#FBBC05" d="M10.4 28.5a14.6 14.6 0 010-9l-7.9-6.2A24 24 0 000 24c0 3.9.9 7.6 2.5 10.8l7.9-6.3z" />
      <path
        fill="#34A853"
        d="M24 48c6.3 0 11.6-2.1 15.4-5.7l-6.8-5.2c-1.9 1.3-4.4 2.1-8.6 2.1-6.3 0-11.6-4.3-13.5-10.1l-7.9 6.3C6.4 42.6 14.6 48 24 48z"
      />
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#1877F2"
        d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.435H7.078V12.07h3.047V9.412c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.492 0-1.956.931-1.956 1.887v2.25h3.328l-.532 3.495h-2.796V24C19.612 23.094 24 18.1 24 12.073z"
      />
    </svg>
  )
}

function AppleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M16.365 1.43c0 1.14-.417 2.2-1.11 2.98-.84.95-2.21 1.69-3.34 1.6-.13-1.1.43-2.27 1.08-3 .73-.82 2.02-1.45 3.07-1.5.03.31.3.59.3.92zM20.5 17.06c-.55 1.27-.81 1.84-1.52 2.96-.99 1.57-2.39 3.52-4.12 3.53-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.98-1.73-.02-3.05-1.78-4.04-3.34-2.77-4.37-3.06-9.5-1.35-12.22 1.21-1.94 3.13-3.07 4.93-3.07 1.84 0 2.99 1.01 4.51 1.01 1.47 0 2.37-1.01 4.5-1.01 1.6 0 3.3.87 4.51 2.38-3.96 2.17-3.32 7.83.68 9.78z"
      />
    </svg>
  )
}

function LinkedInIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#0A66C2"
        d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 110-4.13 2.06 2.06 0 010 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.22.79 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z"
      />
    </svg>
  )
}

export default LoginModal
